import type { Group, User } from "~/client/generated/schema";

type Folder = { id: Readonly<string>; fullName: Readonly<string>; group: Readonly<Partial<Group>> };

const withSeparator = (descriptions) => descriptions.join("、");

const keywords = (intl, words: ReadonlyArray<string>) => {
  if (words.length === 0) {
    return intl.formatMessage({ id: "searchCondition.noKeywords" });
  }
  return words.join(" ");
};

/* eslint-disable @typescript-eslint/naming-convention */
const dateRangeIntlSubIds = {
  "2000-01-01": "all",
  "now-3d/d": "within3Days",
  "now-1w/d": "within1Week",
  "now-1M/d": "within1Month",
  "now-6M/d": "withinHalfYear",
  "now-1y/d": "within1Year",
};
/* eslint-enable @typescript-eslint/naming-convention */
const dateRange = (intl, value: string | undefined | null) => {
  if (!value) {
    return null;
  }
  const subId = dateRangeIntlSubIds[value];
  return intl.formatMessage({ id: `searchDateRangeFilter.${subId}` });
};

const formatResourceType = (intl, subId) => intl.formatMessage({ id: `searchCondition.resourceType.${subId}` });

const normalizeResourceTypes = (origin: ReadonlyArray<string>) => {
  const lowered = [...origin].map((e) => e.toLowerCase());
  const withoutComment = lowered.filter((l) => l !== "comment");
  const hasComment = lowered.includes("comment");
  const replaced = withoutComment
    .sort()
    .join("_")
    .replace("blog_wiki", "note")
    .split("_")
    .filter((r) => r.length > 0);

  if (hasComment) {
    return [...replaced, "comment"];
  }
  return replaced;
};

const resourceTypeName = (intl, resourceTypes: ReadonlyArray<string>) => {
  const normalized = normalizeResourceTypes(resourceTypes);
  if (normalized.length === 0) {
    return formatResourceType(intl, "all");
  }
  return normalized.map((n) => formatResourceType(intl, n)).join(formatResourceType(intl, "separator"));
};

const isDefaultResourceType = (intl, typeName) => typeName === formatResourceType(intl, "all");

const target = (intl, authorUsers: ReadonlyArray<Partial<User>>, resourceTypes: ReadonlyArray<string>) => {
  if (authorUsers.length === 0 && resourceTypes.length === 0) {
    return null;
  }

  const typeName = resourceTypeName(intl, resourceTypes);
  if (authorUsers.length === 0 && isDefaultResourceType(intl, typeName)) {
    return null;
  }

  if (authorUsers.length === 0) {
    return typeName;
  }

  return withSeparator(
    authorUsers.map((a) => intl.formatMessage({ id: "searchCondition.author" }, { name: a.account, type: typeName })),
  );
};

type EntryLocation = { group: Partial<Group>; folderPath: string | null };

const locationsForGroup = (group: Partial<Group>, folders: ReadonlyArray<Folder>): ReadonlyArray<EntryLocation> => {
  const foldersOfGroup = folders.filter((f) => f.group.databaseId === group.databaseId);
  if (foldersOfGroup.length === 0) {
    return [{ group, folderPath: null } as EntryLocation];
  }
  return foldersOfGroup.map((f) => ({ group, folderPath: f.fullName }));
};

const location = (
  intl,
  groups: ReadonlyArray<Partial<Group>>,
  folders: ReadonlyArray<Folder>,
): ReadonlyArray<EntryLocation> => {
  if (groups.length === 0) {
    return [];
  }
  return groups.flatMap((g) => locationsForGroup(g, folders));
};

const archive = (intl, isArchived: boolean) => {
  if (!isArchived) {
    return null;
  }
  return intl.formatMessage({ id: "searchArchivedFilter.archived" });
};

const like = (intl, isLiked: boolean) => {
  if (!isLiked) {
    return null;
  }
  return intl.formatMessage({ id: "searchLikeFilter.likedByMe" });
};

const excludeKeywords = (intl, keywords: ReadonlyArray<string>) => {
  if (keywords.length === 0) {
    return null;
  }
  const withBrackets = keywords
    .map((k) => intl.formatMessage({ id: "searchCondition.excludeKeyword" }, { word: k }))
    .join("");
  return intl.formatMessage({ id: "searchCondition.excludeKeywords" }, { words: withBrackets });
};

export const describeCondition = { keywords, dateRange, excludeKeywords, target, location, archive, like };
